import { useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import { api, OnboardingStatus } from '../../api/client'
import { toast } from 'sonner'
import './StealthIraPrompt.css'

interface StealthIraPromptProps {
    status: OnboardingStatus
    onClose: () => void
}

export default function StealthIraPrompt({ status, onClose }: StealthIraPromptProps) {
    const posthog = usePostHog()
    const [targetAmount, setTargetAmount] = useState('')
    const [targetYear, setTargetYear] = useState(
        status.coverageType ? String(new Date().getFullYear() + 15) : ''
    )
    const [isSaving, setIsSaving] = useState(false)

    const CURRENT_YEAR = new Date().getFullYear()
    const TARGET_YEARS = Array.from({ length: 35 }, (_, i) => String(CURRENT_YEAR + i + 1))

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        const amount = parseFloat(targetAmount.replace(/,/g, ''))
        if (isNaN(amount) || amount <= 0) return
        setIsSaving(true)
        try {
            await api.updateUserProfile({
                stealthIraTarget: amount,
                stealthIraTargetYear: targetYear ? parseInt(targetYear) : undefined,
            })
            posthog.capture('stealth_ira_target_set', {
                targetAmount: amount,
                targetYear: parseInt(targetYear),
            })
            toast.success('Goal saved! Your Stealth IRA target is set.')
            onClose()
        } catch {
            toast.error('Could not save your goal. Please try again.')
        } finally {
            setIsSaving(false)
        }
    }

    const handleMaybeLater = async () => {
        try {
            await api.updateUserProfile({ deferStealthIraPrompt: true })
        } catch {
            // Best-effort
        }
        onClose()
    }

    return (
        <div className="stealth-ira-overlay" role="dialog" aria-modal="true" aria-labelledby="stealth-ira-title">
            <div className="stealth-ira-modal">
                <div className="stealth-ira-header">
                    <h2 id="stealth-ira-title" className="stealth-ira-title">Set Your Stealth IRA Goal</h2>
                    <p className="stealth-ira-subtitle">
                        How much do you ultimately want to reimburse from your HSA?
                        Setting a target helps you see how close you are.
                    </p>
                </div>

                {/* Progress bar preview */}
                <div className="stealth-ira-preview" aria-hidden="true">
                    <div className="preview-bar-track">
                        <div className="preview-bar-fill" style={{ width: '12%' }} />
                    </div>
                    <div className="preview-labels">
                        <span className="preview-label-current">Current balance</span>
                        <span className="preview-label-goal">Your goal →</span>
                    </div>
                </div>

                <form onSubmit={handleSave} className="stealth-ira-form" id="stealth-ira-form">
                    <div className="form-group">
                        <label htmlFor="stealth-ira-amount" className="form-label">
                            Target Reimbursement Amount
                        </label>
                        <div className="input-dollar">
                            <span className="dollar-symbol">$</span>
                            <input
                                id="stealth-ira-amount"
                                type="text"
                                inputMode="numeric"
                                className="form-input"
                                value={targetAmount}
                                onChange={(e) => setTargetAmount(e.target.value.replace(/[^0-9,]/g, ''))}
                                placeholder="50,000"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="stealth-ira-year" className="form-label">By what year?</label>
                        <select
                            id="stealth-ira-year"
                            className="form-select"
                            value={targetYear}
                            onChange={(e) => setTargetYear(e.target.value)}
                        >
                            <option value="">Select a year...</option>
                            {TARGET_YEARS.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    <div className="stealth-ira-actions">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!targetAmount || isSaving}
                            id="stealth-ira-save"
                        >
                            {isSaving ? 'Saving…' : 'Set my goal'}
                        </button>
                        <button
                            type="button"
                            className="btn-link"
                            onClick={handleMaybeLater}
                            id="stealth-ira-later"
                        >
                            Maybe later
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
